/**
 * FE-PR-20 — pure shop checkout / collect view-model helpers.
 * No React hooks, no side effects, no API calls.
 */
import type {
  LocalizedNameMap,
  PaymentSurfaceReadiness,
  PickupHandoffModeV3,
  SessionMetadataCollect,
} from 'pi-kiosk-shared';
import {
  countCustomerPayableVerifiedMethods,
  isReservedSalesPointSlug,
} from 'pi-kiosk-shared';
import type { CheckoutSubMode } from '../../checkout/checkoutApi.js';
import {
  resolveLockedCollectTiming,
  resolvePickupHandoffModeForCheckout,
} from '../../checkout/checkoutCollectHelpers.js';
import type { PickupSlotSelection } from '../../checkout/PickupSlotPicker.js';
import type { CatalogProduct, ShopCartLine } from '../types.js';

export const SHOP_DEBOUNCE_DUPLICATE_DECODE_MS = 500;
export const SHOP_SLA_TOAST_DURATION_MS = 4000;
export const SHOP_SCAN_FEEDBACK_CLEAR_MS = 5000;
export const SHOP_SLUG_REDIRECT_DELAY_MS = 300;
/** Appendix D — sales-point capability snapshot TTL before pay CTA. */
export const SALES_POINT_CAPABILITY_TTL_MS = 60_000;

export type ProductCheckoutStep = 'collect' | 'payment';

export type CustomerPaymentMethodId = 'QR' | 'CARD' | 'WALLET' | 'CASH';

export interface ShopKioskMetaSource {
  readonly enabledMobileFirstCheckoutSubModes?: readonly string[];
  readonly allowedPickupHandoffModes?: readonly PickupHandoffModeV3[];
  readonly commerceConfigJson?: unknown;
  readonly defaultPickupHandoffMode?: PickupHandoffModeV3;
  readonly payOnSpotProximityNoticeMinutes?: number;
  readonly code?: string | null;
  /** Display name when available on public sales-point config (G1 fallback label). */
  readonly name?: string | null;
  readonly kioskId?: number;
  readonly redirectTo?: string;
  readonly payableVerifiedMethodCount?: number | null;
  readonly paymentSurfaceReadiness?: PaymentSurfaceReadiness | null;
  readonly currencyAcceptanceMode?: 'SINGLE' | 'MULTI';
  readonly acceptedCurrencies?: readonly string[];
}

export interface PickupPointOption {
  readonly id: number;
  readonly isOnSpot?: boolean;
}

export function formatSalesPointCapabilityVersion(
  meta: ShopKioskMetaSource | null | undefined,
): string | undefined {
  const modes = meta?.enabledMobileFirstCheckoutSubModes;
  if (modes === undefined || modes.length === 0) {
    return undefined;
  }
  return [...modes].sort().join('+');
}


export function resolveKioskIdFromRoute(input: {
  readonly kioskSlug: string | undefined;
  readonly slugKioskId: number | undefined;
  readonly idKioskId: number | undefined;
  readonly fallbackKioskId: number | null;
}): number | null {
  if (input.kioskSlug !== undefined && input.kioskSlug.length > 0) {
    return input.slugKioskId ?? null;
  }
  return input.idKioskId ?? input.fallbackKioskId;
}

export function resolveInactiveSlugError(input: {
  readonly kioskSlug: string | undefined;
  readonly slugLookupFailed: boolean;
  readonly slugLookupStatus: number | undefined;
}): boolean {
  return (
    input.kioskSlug !== undefined &&
    input.kioskSlug.length > 0 &&
    input.slugLookupFailed &&
    input.slugLookupStatus === 410
  );
}

/** 410 inactive sales point from `GET /customer/sales-points/by-id/:id` (`?kioskId=`). */
export function resolveInactiveIdError(input: {
  readonly kioskSlug: string | undefined;
  readonly queryKioskId: number | null;
  readonly idLookupFailed: boolean;
  readonly idLookupStatus: number | undefined;
}): boolean {
  return (
    (input.kioskSlug === undefined || input.kioskSlug.length === 0) &&
    input.queryKioskId !== null &&
    input.idLookupFailed &&
    input.idLookupStatus === 410
  );
}

export type InactiveKioskLookupSource = 'slug' | 'id';

export function resolveInactiveKioskLookup(input: {
  readonly kioskSlug: string | undefined;
  readonly slugLookupFailed: boolean;
  readonly slugLookupStatus: number | undefined;
  readonly queryKioskId: number | null;
  readonly idLookupFailed: boolean;
  readonly idLookupStatus: number | undefined;
}): InactiveKioskLookupSource | null {
  if (resolveInactiveSlugError(input)) {
    return 'slug';
  }
  if (resolveInactiveIdError(input)) {
    return 'id';
  }
  return null;
}

export interface InactiveKioskLookupPayload {
  readonly name?: string;
  readonly nameLocales?: LocalizedNameMap | null;
  readonly code?: string;
}

/** Parse KIOSK_INACTIVE 410 body from slug or by-id lookup errors. */
export function extractInactiveKioskPayload(
  error: unknown,
): InactiveKioskLookupPayload | null {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('status' in error) ||
    (error as { status: number }).status !== 410
  ) {
    return null;
  }
  const envelope = error as unknown as { data?: unknown };
  const body = envelope.data as { data?: InactiveKioskLookupPayload } | undefined;
  return body?.data ?? null;
}

export function resolveInactiveKioskFallbackLabel(input: {
  readonly lookup: 'slug' | 'id';
  readonly kioskSlug: string | undefined;
  readonly kioskId: number | null;
}): string | undefined {
  if (input.lookup === 'slug') {
    return input.kioskSlug;
  }
  if (input.kioskId !== null) {
    return String(input.kioskId);
  }
  return undefined;
}

function resolveKioskSlugFromRoute(
  kioskSlugParam: string | undefined,
  slugFromQuery: string,
): string | undefined {
  if (kioskSlugParam !== undefined && kioskSlugParam.length > 0) {
    // Reserved hubs must never bind as store slug (e.g. /shops/{x} → fake SP).
    if (isReservedSalesPointSlug(kioskSlugParam)) {
      return undefined;
    }
    return kioskSlugParam;
  }
  if (slugFromQuery.length > 0) {
    if (isReservedSalesPointSlug(slugFromQuery)) {
      return undefined;
    }
    return slugFromQuery;
  }
  return undefined;
}

export { resolveKioskSlugFromRoute };

/** Redirect to `/{tenant}/shops` only for unknown/invalid slug — not inactive (410) or transient errors. */
export function shouldRedirectUnknownKioskSlugToShops(input: {
  readonly kioskSlug: string | undefined;
  readonly slugLookupFailed: boolean;
  readonly slugLookupStatus: number | undefined;
}): boolean {
  return (
    input.kioskSlug !== undefined &&
    input.kioskSlug.length > 0 &&
    input.slugLookupFailed &&
    (input.slugLookupStatus === 404 || input.slugLookupStatus === 422)
  );
}

export function isSelfCollectOnlyGate(input: {
  readonly enabledMobileFirstCheckoutSubModes: readonly CheckoutSubMode[] | readonly string[];
  readonly rawEnabledMobileFirstCheckoutSubModes?:
    | readonly CheckoutSubMode[]
    | readonly string[]
    | null;
  readonly scheduledPickupEnabled: boolean;
  /**
   * Immediate self-pickup entitlement (forced On when pickup infra is Off for product tenants).
   * When Off, do not require the on-site / kiosk presence checkbox.
   */
  readonly immediateSelfPickupEntitled?: boolean;
}): boolean {
  if (input.immediateSelfPickupEntitled === false) {
    return false;
  }
  const fallbackModes = input.enabledMobileFirstCheckoutSubModes;
  const rawModes = input.rawEnabledMobileFirstCheckoutSubModes;
  const sourceModes =
    rawModes !== undefined && rawModes !== null && rawModes.length > 0 ? rawModes : fallbackModes;
  return (
    sourceModes.length === 1 &&
    sourceModes[0] === 'PAY_NOW_SELF_SERVICE' &&
    !input.scheduledPickupEnabled
  );
}

export function resolveInitialProductCheckoutStep(input: {
  readonly purposeType: 'PRODUCT' | 'DONATION';
  readonly collect: SessionMetadataCollect | null | undefined;
  readonly sessionStatus?: 'OPEN' | 'AWAITING_PAYMENT' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
  /** G9 — SHIPPING sessions omit collect; go straight to payment. */
  readonly fulfillmentMode?: 'PICKUP' | 'SHIPPING' | null;
}): ProductCheckoutStep {
  if (input.purposeType === 'DONATION') {
    return 'payment';
  }
  if (input.fulfillmentMode === 'SHIPPING') {
    return 'payment';
  }
  // Known-unusable (null / LATER-without-slot) must not first-paint payment —
  // including AWAITING_PAYMENT. `undefined` = evidence not loaded yet.
  if (input.collect !== undefined && !isUsableCheckoutSessionCollect(input.collect)) {
    return 'collect';
  }
  if (input.sessionStatus === 'AWAITING_PAYMENT') {
    return 'payment';
  }
  if (!isUsableCheckoutSessionCollect(input.collect)) {
    return 'collect';
  }
  return 'payment';
}

/**
 * Remount / session-bind step restore.
 * - MOB-05: intentional persisted collect|payment survives remount.
 * - Unusable collect → force collect; `falseCollectForceAction: 'mark'`.
 * - `collect === undefined` = evidence not loaded — do not rewrite; action `'keep'`
 *   (except qr→payment demote, which always clears force + rewrites like leave-off).
 * - Cold false-collect recovery: only when `falseCollectForce` + known usable → payment
 *   (`falseCollectForceAction: 'clear'`). Intentional Back clears via persist, not here.
 * - Persisted `qr` demotes to UI `payment` with the same clear+rewrite as
 *   `persistCheckoutQrDemoteToPayment` so remount cannot leave orphan force.
 */
export function resolveRestoredProductCheckoutStep(input: {
  readonly persistedInFlowStep: 'collect' | 'payment' | 'qr' | null;
  readonly purposeType: 'PRODUCT' | 'DONATION';
  readonly collect: SessionMetadataCollect | null | undefined;
  readonly sessionStatus?: 'OPEN' | 'AWAITING_PAYMENT' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED';
  /** True when prior restore forced collect due to unusable evidence (not intentional Back). */
  readonly falseCollectForce?: boolean;
  /** G9 — SHIPPING sessions omit collect; do not force collect step. */
  readonly fulfillmentMode?: 'PICKUP' | 'SHIPPING' | null;
}): {
  readonly step: ProductCheckoutStep;
  readonly rewriteInFlowTo: 'collect' | 'payment' | null;
  /** mark = set discriminator; clear = drop it; keep = leave sessionStorage alone. */
  readonly falseCollectForceAction: 'mark' | 'clear' | 'keep';
} {
  const isShipping = input.fulfillmentMode === 'SHIPPING';
  const persisted = input.persistedInFlowStep;
  let restored: ProductCheckoutStep;
  if (isShipping) {
    restored = persisted === 'qr' ? 'payment' : persisted === 'collect' || persisted === 'payment' ? 'payment' : 'payment';
  } else if (persisted === 'collect' || persisted === 'payment') {
    restored = persisted;
  } else if (persisted === 'qr') {
    restored = 'payment';
  } else {
    restored = resolveInitialProductCheckoutStep({
      purposeType: input.purposeType,
      collect: input.collect,
      sessionStatus: input.sessionStatus,
      fulfillmentMode: input.fulfillmentMode,
    });
  }

  if (
    !isShipping &&
    restored === 'payment' &&
    input.purposeType !== 'DONATION' &&
    input.collect !== undefined &&
    !isUsableCheckoutSessionCollect(input.collect)
  ) {
    return {
      step: 'collect',
      rewriteInFlowTo: 'collect',
      falseCollectForceAction: 'mark',
    };
  }

  if (
    restored === 'collect' &&
    input.falseCollectForce === true &&
    input.purposeType !== 'DONATION' &&
    input.collect !== undefined &&
    isUsableCheckoutSessionCollect(input.collect)
  ) {
    return {
      step: 'payment',
      rewriteInFlowTo: 'payment',
      falseCollectForceAction: 'clear',
    };
  }

  // Known-unusable on collect → keep/set force so a later usable bind can promote.
  // `undefined` must keep existing flag (cold init before bind).
  if (
    restored === 'collect' &&
    input.purposeType !== 'DONATION' &&
    input.collect !== undefined &&
    !isUsableCheckoutSessionCollect(input.collect)
  ) {
    return {
      step: 'collect',
      rewriteInFlowTo: null,
      falseCollectForceAction: 'mark',
    };
  }

  // qr→payment demote must match leave-off / Back (`persistCheckoutQrDemoteToPayment`):
  // rewrite in-flow to payment and clear orphan falseCollectForce.
  if (persisted === 'qr' && restored === 'payment') {
    return {
      step: 'payment',
      rewriteInFlowTo: 'payment',
      falseCollectForceAction: 'clear',
    };
  }

  return {
    step: restored,
    rewriteInFlowTo: null,
    falseCollectForceAction: 'keep',
  };
}

/**
 * G3 — after first session bind, re-promote collect→payment only for cold
 * false-collect recovery (force flag + known usable). Intentional Back clears
 * the flag (MOB-05). Optional `currentStep` avoids clobbering qr/payment.
 */
export function shouldPromoteFalseCollectRecovery(input: {
  readonly falseCollectForce: boolean;
  readonly purposeType: 'PRODUCT' | 'DONATION';
  readonly collect: SessionMetadataCollect | null | undefined;
  readonly currentStep?: ProductCheckoutStep;
}): boolean {
  if (input.falseCollectForce !== true) {
    return false;
  }
  if (input.purposeType === 'DONATION') {
    return false;
  }
  if (input.currentStep !== undefined && input.currentStep !== 'collect') {
    return false;
  }
  if (input.collect === undefined) {
    return false;
  }
  return isUsableCheckoutSessionCollect(input.collect);
}

/** G4/G5 — microtask promote with live collect re-read at execution (MOB-05 safe). */
export type ScheduleFalseCollectRecoveryPromoteInput = {
  readonly sid: string;
  readonly purposeType: 'PRODUCT' | 'DONATION';
  /** Live collect at microtask — do not close over a schedule-time snapshot. */
  readonly readCollect: () => SessionMetadataCollect | null | undefined;
  readonly readFalseCollectForce: (sessionId: string) => boolean;
  readonly readCurrentStep: () => ProductCheckoutStep;
  readonly writeInFlowPayment: (sessionId: string) => void;
  readonly clearFalseCollectForce: (sessionId: string) => void;
  readonly onPromote: (input: {
    readonly nextCollect: SessionMetadataCollect | null;
    readonly nextSelfCollect: boolean;
  }) => void;
};

export function scheduleFalseCollectRecoveryPromote(
  input: ScheduleFalseCollectRecoveryPromoteInput,
): void {
  const {
    sid,
    purposeType,
    readCollect,
    readFalseCollectForce,
    readCurrentStep,
    writeInFlowPayment,
    clearFalseCollectForce,
    onPromote,
  } = input;
  queueMicrotask(() => {
    const liveCollect = readCollect();
    if (
      !shouldPromoteFalseCollectRecovery({
        falseCollectForce: readFalseCollectForce(sid),
        purposeType,
        collect: liveCollect,
        currentStep: readCurrentStep(),
      })
    ) {
      return;
    }
    const nextCollect = liveCollect ?? null;
    const nextSelfCollect = liveCollect?.selfCollectConfirmedAtKiosk === true;
    writeInFlowPayment(sid);
    clearFalseCollectForce(sid);
    onPromote({ nextCollect, nextSelfCollect });
  });
}

export function isSalesPointCapabilityStale(input: {
  readonly fetchedAtMs: number | null;
  readonly nowMs: number;
  readonly ttlMs?: number;
}): boolean {
  if (input.fetchedAtMs === null) {
    return true;
  }
  const ttlMs = input.ttlMs ?? SALES_POINT_CAPABILITY_TTL_MS;
  return input.nowMs - input.fetchedAtMs >= ttlMs;
}

export function shouldInvalidateCheckoutOnSalesPointChange(input: {
  readonly sessionSalesPointId: number | null;
  readonly currentSalesPointId: number | null;
}): boolean {
  if (input.sessionSalesPointId === null || input.currentSalesPointId === null) {
    return false;
  }
  return input.sessionSalesPointId !== input.currentSalesPointId;
}

export function resolveOrderedPaymentMethods(input: {
  readonly bankQr: boolean;
  readonly gateway: boolean;
  readonly walletPaymentsEnabled: boolean;
  readonly cash?: boolean;
}): readonly CustomerPaymentMethodId[] {
  const methods: CustomerPaymentMethodId[] = [];
  if (input.bankQr) {
    methods.push('QR');
  }
  if (input.gateway) {
    methods.push('CARD');
  }
  if (input.cash === true) {
    methods.push('CASH');
  }
  // v1.1 wallet rails — no paymentRailsMobile wallet slot today; flag gates future UI only.
  if (input.walletPaymentsEnabled) {
    methods.push('WALLET');
  }
  return methods;
}

/** Phase E — prefer API readiness; fall back to visible methods; wallet gated by v1.1 flag.
 * Customer always requireVerified (ignore env false) — fail-closed on null readiness via callers.
 */
export function resolveCustomerPayableVerifiedMethodCount(input: {
  readonly paymentSurfaceReadiness: PaymentSurfaceReadiness | null | undefined;
  readonly payableVerifiedMethodCount: number | null | undefined;
  readonly visiblePaymentMethodCount: number;
  readonly walletPaymentsEnabled: boolean;
  readonly runtimeRequireVerified?: boolean;
}): number {
  // Customer listing+checkout always requireVerified (product lock).
  const requireVerified = true;
  void input.runtimeRequireVerified;
  if (input.paymentSurfaceReadiness !== null && input.paymentSurfaceReadiness !== undefined) {
    return countCustomerPayableVerifiedMethods(input.paymentSurfaceReadiness.methods, {
      requireVerified,
      walletPaymentsEnabled: input.walletPaymentsEnabled,
    });
  }
  if (input.payableVerifiedMethodCount !== null && input.payableVerifiedMethodCount !== undefined) {
    return input.payableVerifiedMethodCount;
  }
  return input.visiblePaymentMethodCount;
}

/**
 * Sales-point commerce may still list PREPAY_COLLECT_LATER while tenant
 * `scheduled_pickup` (or pickup infra) is Off. Never offer that journey when
 * scheduled pickup is disabled — otherwise continue hits SCHEDULED_PICKUP_NOT_ENABLED.
 */
export function resolveAvailableCheckoutModes(
  kioskMeta: ShopKioskMetaSource | null | undefined,
  scheduledPickupEnabled = true,
): CheckoutSubMode[] {
  const fromKiosk = kioskMeta?.enabledMobileFirstCheckoutSubModes;
  let raw: CheckoutSubMode[];
  if (fromKiosk !== undefined && fromKiosk.length > 0) {
    raw = fromKiosk.filter(
      (mode): mode is CheckoutSubMode =>
        mode === 'PREPAY_COLLECT_LATER' || mode === 'PAY_NOW_SELF_SERVICE',
    );
  } else if (scheduledPickupEnabled) {
    raw = ['PREPAY_COLLECT_LATER'];
  } else {
    raw = ['PAY_NOW_SELF_SERVICE'];
  }

  const entitled = scheduledPickupEnabled
    ? raw
    : raw.filter((mode) => mode !== 'PREPAY_COLLECT_LATER');

  if (entitled.length > 0) {
    return entitled;
  }
  return ['PAY_NOW_SELF_SERVICE'];
}

export function resolveEffectiveJourneyMode(
  selectedJourneyMode: CheckoutSubMode | null,
  availableCheckoutModes: readonly CheckoutSubMode[],
): CheckoutSubMode {
  if (
    selectedJourneyMode !== null &&
    availableCheckoutModes.includes(selectedJourneyMode)
  ) {
    return selectedJourneyMode;
  }
  return availableCheckoutModes[0] ?? 'PAY_NOW_SELF_SERVICE';
}

const SELF_PICKUP_HANDOFF_MODES = new Set<PickupHandoffModeV3>([
  'AUTO_ON_PAYMENT',
  'CUSTOMER_TAP',
]);

export function isSelfPickupHandoffModeForJourney(mode: PickupHandoffModeV3): boolean {
  return SELF_PICKUP_HANDOFF_MODES.has(mode);
}

export function resolveAllowedPickupHandoffModes(
  kioskMeta: ShopKioskMetaSource | null | undefined,
): readonly PickupHandoffModeV3[] {
  const fromKiosk = kioskMeta?.allowedPickupHandoffModes;
  if (fromKiosk !== undefined && fromKiosk.length > 0) {
    return fromKiosk;
  }
  return ['AUTO_ON_PAYMENT', 'STAFF_SCAN'];
}

/**
 * Mobile ASAP / pay-now cannot use STAFF_SCAN (backend CHECKOUT_JOURNEY_INVALID).
 * Prepay-later cannot use AUTO_ON_PAYMENT. Filter capability lists per journey.
 */
export function resolveAllowedPickupHandoffModesForJourney(
  journeyMode: CheckoutSubMode,
  allowedPickupHandoffModes: readonly PickupHandoffModeV3[],
): readonly PickupHandoffModeV3[] {
  if (journeyMode === 'PAY_NOW_SELF_SERVICE') {
    const selfModes = allowedPickupHandoffModes.filter(isSelfPickupHandoffModeForJourney);
    return selfModes.length > 0 ? selfModes : ['AUTO_ON_PAYMENT'];
  }
  if (journeyMode === 'PREPAY_COLLECT_LATER') {
    const barModes = allowedPickupHandoffModes.filter(
      (mode) => !isSelfPickupHandoffModeForJourney(mode),
    );
    return barModes.length > 0 ? barModes : ['STAFF_SCAN'];
  }
  return allowedPickupHandoffModes;
}

export function resolveDefaultHandoffMode(input: {
  readonly effectiveJourneyMode: CheckoutSubMode;
  readonly kioskMeta: ShopKioskMetaSource | null | undefined;
  readonly allowedPickupHandoffModes: readonly PickupHandoffModeV3[];
}): PickupHandoffModeV3 {
  const journeyAllowed = resolveAllowedPickupHandoffModesForJourney(
    input.effectiveJourneyMode,
    input.allowedPickupHandoffModes,
  );
  const configured = resolvePickupHandoffModeForCheckout(
    input.effectiveJourneyMode,
    (input.kioskMeta?.commerceConfigJson ?? null) as Record<string, unknown> | null,
  );
  if (journeyAllowed.includes(configured)) {
    return configured;
  }
  const fromCapabilities = input.kioskMeta?.defaultPickupHandoffMode;
  if (
    fromCapabilities !== undefined &&
    journeyAllowed.includes(fromCapabilities)
  ) {
    return fromCapabilities;
  }
  return journeyAllowed[0] ?? configured;
}

export function resolveEffectiveHandoffMode(input: {
  readonly selectedHandoffMode: PickupHandoffModeV3 | null;
  readonly defaultHandoffMode: PickupHandoffModeV3;
  readonly effectiveJourneyMode: CheckoutSubMode;
}): PickupHandoffModeV3 {
  const candidate = input.selectedHandoffMode ?? input.defaultHandoffMode;
  const journeyAllowed = resolveAllowedPickupHandoffModesForJourney(
    input.effectiveJourneyMode,
    [candidate, input.defaultHandoffMode],
  );
  if (journeyAllowed.includes(candidate)) {
    return candidate;
  }
  return input.defaultHandoffMode;
}

export function resolveDefaultCollectSelection(
  pickupPoints: readonly PickupPointOption[] | undefined,
  lockedCollectTiming: SessionMetadataCollect['timing'],
): SessionMetadataCollect | null {
  if (pickupPoints === undefined || pickupPoints.length === 0) {
    return null;
  }
  const defaultPoint = pickupPoints.find((point) => point.isOnSpot) ?? pickupPoints[0];
  if (defaultPoint === undefined) {
    return null;
  }
  return { timing: lockedCollectTiming, pickupPointId: defaultPoint.id };
}

/**
 * Session collect that payment start will accept (readCollectMetadata non-null +
 * CollectMetadataPolicy LATER slot when timing is LATER).
 */
export function isUsableCheckoutSessionCollect(
  collect: SessionMetadataCollect | null | undefined,
): boolean {
  if (collect == null || !(collect.pickupPointId > 0)) {
    return false;
  }
  if (collect.timing === 'NOW') {
    return true;
  }
  const promised = collect.pickup?.promisedPickupAt?.trim() ?? '';
  return promised.length > 0;
}

export function resolveEffectiveCollectSelection(input: {
  readonly collectSelection: SessionMetadataCollect | null;
  readonly defaultCollectSelection: SessionMetadataCollect | null;
  readonly lockedCollectTiming: SessionMetadataCollect['timing'];
}): SessionMetadataCollect | null {
  const base = input.collectSelection ?? input.defaultCollectSelection;
  if (base === null) {
    return null;
  }
  // Buy Again ORDER coerces LATER-without-slot → NOW while keeping PREPAY_COLLECT_LATER
  // mode. Mode lock would rewrite NOW→LATER without a slot; persist-before-pay then
  // PATCHes COLLECT_METADATA_REQUIRED and payment UI has no collect step to recover.
  if (
    input.lockedCollectTiming === 'LATER' &&
    base.timing === 'NOW' &&
    isUsableCheckoutSessionCollect(base)
  ) {
    return {
      timing: 'NOW',
      pickupPointId: base.pickupPointId,
    };
  }
  const { selfCollectConfirmedAtKiosk: _kioskAttestation, ...customerCollect } = base;
  return {
    ...customerCollect,
    timing: input.lockedCollectTiming,
    ...(input.lockedCollectTiming === 'NOW' ? {} : { pickup: base.pickup }),
  };
}

/**
 * G2 — Collect sheet display/emit timing: do not prefer locked LATER over usable
 * session NOW (would emit slot-less LATER and recreate COLLECT_METADATA_REQUIRED).
 */
export function resolveCollectSheetDisplayTiming(input: {
  readonly lockedCollectTiming: SessionMetadataCollect['timing'] | undefined;
  readonly collect: SessionMetadataCollect | null;
}): SessionMetadataCollect['timing'] {
  if (
    input.lockedCollectTiming === 'LATER' &&
    input.collect != null &&
    input.collect.timing === 'NOW' &&
    isUsableCheckoutSessionCollect(input.collect)
  ) {
    return 'NOW';
  }
  return input.lockedCollectTiming ?? input.collect?.timing ?? 'NOW';
}

/**
 * G2 — Whether the collect sheet should show the NOW radio under a LATER mode lock
 * because session already has pay-ready NOW (Buy Again coerce).
 */
export function shouldShowNowTimingUnderLaterLock(
  lockedCollectTiming: SessionMetadataCollect['timing'] | undefined,
  collect: SessionMetadataCollect | null,
): boolean {
  return (
    lockedCollectTiming === 'LATER' &&
    collect != null &&
    collect.timing === 'NOW' &&
    isUsableCheckoutSessionCollect(collect)
  );
}

export function resolveEffectivePickupSlot(
  pickupPoints: readonly PickupPointOption[] | undefined,
  pickupSlot: PickupSlotSelection | null,
): PickupSlotSelection | null {
  if (pickupPoints === undefined || pickupPoints.length === 0) {
    return null;
  }
  return pickupSlot;
}

export interface CartStaleState {
  readonly staleLineKeys: ReadonlySet<string>;
  readonly hasQtyExceedsStock: boolean;
}

function resolveLineStock(
  line: ShopCartLine,
  stockByProduct: ReadonlyMap<number, number>,
  stockByVariant: ReadonlyMap<string, number>,
): number | undefined {
  if (line.variant !== null) {
    return stockByVariant.get(`${line.product.id}:${line.variant.id}`);
  }
  return stockByProduct.get(line.product.id);
}

export function computeCartStaleState(
  cartLines: readonly ShopCartLine[],
  products: readonly CatalogProduct[] | undefined,
): CartStaleState {
  if (products === undefined) {
    return {
      staleLineKeys: new Set(cartLines.map((line) => line.key)),
      hasQtyExceedsStock: false,
    };
  }
  const stockByProduct = new Map<number, number>();
  const stockByVariant = new Map<string, number>();
  for (const product of products) {
    stockByProduct.set(product.id, product.quantityInStock);
    const variants = product.variants;
    if (variants !== undefined) {
      for (const variant of variants) {
        stockByVariant.set(`${product.id}:${variant.id}`, variant.quantityInStock);
      }
    }
  }
  const stale = new Set<string>();
  let hasQtyExceedsStock = false;
  for (const line of cartLines) {
    const stock = resolveLineStock(line, stockByProduct, stockByVariant);
    if (stock === undefined || stock <= 0) {
      stale.add(line.key);
      continue;
    }
    if (line.quantity > stock) {
      stale.add(line.key);
      hasQtyExceedsStock = true;
    }
  }
  return { staleLineKeys: stale, hasQtyExceedsStock };
}

export function computeStaleLineKeys(
  cartLines: readonly ShopCartLine[],
  products: readonly CatalogProduct[] | undefined,
): ReadonlySet<string> {
  return computeCartStaleState(cartLines, products).staleLineKeys;
}

export function canOpenCheckout(input: {
  readonly kioskId: number | null;
  readonly totalAmountMinor: number;
  readonly payDisabled: boolean;
}): boolean {
  return input.kioskId !== null && input.totalAmountMinor > 0 && !input.payDisabled;
}

export function resolveCollectPayloadForCheckout(input: {
  readonly effectiveCollectSelection: SessionMetadataCollect | null;
  readonly effectivePickupSlot: PickupSlotSelection | null;
  readonly selfCollectOnlyGate?: boolean;
  readonly selfCollectConfirmed?: boolean;
}): SessionMetadataCollect | null {
  if (input.effectiveCollectSelection === null) {
    return null;
  }
  const base =
    input.effectiveCollectSelection.timing !== 'LATER' ||
    input.effectivePickupSlot === null
      ? input.effectiveCollectSelection
      : {
          timing: 'LATER' as const,
          pickupPointId: input.effectiveCollectSelection.pickupPointId,
          pickup: {
            promisedPickupAt: input.effectivePickupSlot.startAt,
            pickupWindowEndAt: input.effectivePickupSlot.endAt,
          },
        };
  // G3/G22 — selfCollectConfirmedAtKiosk is kiosk-only; customer shop never persists it.
  if (base.selfCollectConfirmedAtKiosk !== true) {
    return base;
  }
  const { selfCollectConfirmedAtKiosk: _kioskAttestation, ...customerCollect } = base;
  return customerCollect;
}

export function shouldBlockCollectContinue(input: {
  readonly collectPayload: SessionMetadataCollect | null;
  readonly lockedCollectTiming: SessionMetadataCollect['timing'];
  readonly scheduledPickupEnabled: boolean;
  readonly effectivePickupSlot: PickupSlotSelection | null;
  readonly selfCollectOnlyGate?: boolean;
  readonly selfCollectConfirmed?: boolean;
}): boolean {
  if (input.collectPayload === null) {
    return true;
  }
  // G1 — Buy Again PREPAY+NOW: usable NOW under LATER mode lock must Continue
  // (same carve-out as resolveEffectiveCollectSelection). Includes scheduled off.
  const usableNowUnderLaterLock =
    input.lockedCollectTiming === 'LATER' &&
    input.collectPayload.timing === 'NOW' &&
    isUsableCheckoutSessionCollect(input.collectPayload);
  if (
    input.collectPayload.timing !== input.lockedCollectTiming &&
    !usableNowUnderLaterLock
  ) {
    return true;
  }
  if (input.collectPayload.timing === 'LATER' && !input.scheduledPickupEnabled) {
    return true;
  }
  if (
    input.collectPayload.timing === 'LATER' &&
    input.scheduledPickupEnabled &&
    input.effectivePickupSlot === null
  ) {
    return true;
  }
  if (input.selfCollectOnlyGate === true && input.selfCollectConfirmed !== true) {
    return true;
  }
  return false;
}

export function shouldShowSelfServiceSlaToast(
  journeyMode: CheckoutSubMode,
  collectTiming: SessionMetadataCollect['timing'],
): boolean {
  return journeyMode === 'PAY_NOW_SELF_SERVICE' && collectTiming === 'NOW';
}

export function shouldDebounceBarcodeDecode(input: {
  readonly lastRaw: string | null;
  readonly lastAt: number | null;
  readonly raw: string;
  readonly now: number;
  readonly debounceMs?: number;
}): boolean {
  if (input.lastRaw === null || input.lastAt === null) {
    return false;
  }
  const debounceMs = input.debounceMs ?? SHOP_DEBOUNCE_DUPLICATE_DECODE_MS;
  return input.lastRaw === input.raw && input.now - input.lastAt < debounceMs;
}

export function buildVariantSuffix(variantName: string | null | undefined): string {
  if (variantName !== undefined && variantName !== null) {
    return ` (${variantName})`;
  }
  return '';
}

export function buildSlugRedirectTarget(tenantCode: string, code: string): string {
  return `/${encodeURIComponent(tenantCode)}/${encodeURIComponent(code)}`;
}

/**
 * Checkout navigate path. Optional Self-Scan context (`selfScan=1`, basketPublicId)
 * must survive currency-switch remounts so payment rails keep Stage 1–4 CASH.
 */
export function buildCheckoutNavigatePath(
  tenantCode: string,
  sessionId: string,
  options?: {
    readonly selfScan?: boolean;
    readonly basketPublicId?: string | null;
    /** Preserve Self-Scan query keys from current `location.search`. */
    readonly preserveFromSearch?: string;
  },
): string {
  const params = new URLSearchParams();
  params.set('sessionId', sessionId);

  let selfScan = options?.selfScan === true;
  let basketPublicId =
    options?.basketPublicId !== undefined &&
    options.basketPublicId !== null &&
    options.basketPublicId.trim().length > 0
      ? options.basketPublicId.trim()
      : null;

  const rawSearch = options?.preserveFromSearch;
  if (rawSearch !== undefined && rawSearch.length > 0) {
    const existing = new URLSearchParams(
      rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch,
    );
    const flag = existing.get('selfScan');
    if (flag === '1' || flag === 'true') {
      selfScan = true;
    }
    const basket = existing.get('basketPublicId')?.trim() ?? '';
    if (basket.length > 0) {
      basketPublicId = basket;
    }
  }

  if (selfScan) {
    params.set('selfScan', '1');
  }
  if (basketPublicId !== null) {
    params.set('basketPublicId', basketPublicId);
  }

  return `/${encodeURIComponent(tenantCode)}/checkout?${params.toString()}`;
}

export function mapCartLinesToCheckoutSessionLines(
  cartLines: readonly ShopCartLine[],
): Array<{
  productId: number;
  variantId: number | null;
  quantity: number;
}> {
  return cartLines.map((line) => ({
    productId: line.product.id,
    variantId: line.variant?.id ?? null,
    quantity: line.quantity,
  }));
}

export function resolveLockedCollectTimingForMode(
  effectiveJourneyMode: CheckoutSubMode,
): SessionMetadataCollect['timing'] {
  return resolveLockedCollectTiming(effectiveJourneyMode);
}

export function resolveSalesPointSlugForRemember(input: {
  readonly kioskSlug: string | undefined;
  readonly slugFromSlugQuery: string | null | undefined;
  readonly slugFromIdQuery: string | null | undefined;
}): string {
  const slugFromRoute = input.kioskSlug?.trim() ?? '';
  if (slugFromRoute.length > 0) {
    return slugFromRoute;
  }
  return input.slugFromSlugQuery?.trim() ?? input.slugFromIdQuery?.trim() ?? '';
}

export function shouldWaitForSlugKioskId(
  kioskSlug: string | undefined,
  kioskId: number | null,
): boolean {
  const slugRoute = kioskSlug !== undefined && kioskSlug.length > 0;
  return slugRoute && kioskId === null;
}
